import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLanguage } from '@/context/LanguageContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Extended transaction data
const allTransactions = [
  { id: 1, type: 'buy', coin: 'BTC', coinName: 'Bitcoin', amount: 0.025, value: 1680.86, fee: 2.50, status: 'completed', date: '2024-01-15', time: '14:32:45' },
  { id: 2, type: 'sell', coin: 'ETH', coinName: 'Ethereum', amount: 1.5, value: 4868.67, fee: 7.30, status: 'completed', date: '2024-01-15', time: '12:15:30' },
  { id: 3, type: 'swap', coin: 'SOL', toCoin: 'USDT', coinName: 'Solana', amount: 10, value: 1425.60, fee: 1.42, status: 'pending', date: '2024-01-15', time: '10:45:00' },
  { id: 4, type: 'buy', coin: 'ADA', coinName: 'Cardano', amount: 500, value: 228.35, fee: 0.34, status: 'completed', date: '2024-01-14', time: '22:10:15' },
  { id: 5, type: 'sell', coin: 'BNB', coinName: 'BNB', amount: 2, value: 1196.46, fee: 1.79, status: 'failed', date: '2024-01-14', time: '18:55:00' },
  { id: 6, type: 'buy', coin: 'DOGE', coinName: 'Dogecoin', amount: 1000, value: 123.40, fee: 0.18, status: 'completed', date: '2024-01-14', time: '15:20:30' },
  { id: 7, type: 'swap', coin: 'ETH', toCoin: 'BTC', coinName: 'Ethereum', amount: 2.5, value: 8122.45, fee: 12.18, status: 'completed', date: '2024-01-13', time: '09:45:00' },
  { id: 8, type: 'buy', coin: 'SOL', coinName: 'Solana', amount: 25, value: 3564.00, fee: 5.35, status: 'completed', date: '2024-01-12', time: '16:30:00' },
  { id: 9, type: 'sell', coin: 'XRP', coinName: 'XRP', amount: 5000, value: 2617.00, fee: 3.93, status: 'completed', date: '2024-01-12', time: '11:15:00' },
  { id: 10, type: 'buy', coin: 'MATIC', coinName: 'Polygon', amount: 2500, value: 1625.00, fee: 2.44, status: 'completed', date: '2024-01-11', time: '20:00:00' },
  { id: 11, type: 'swap', coin: 'BTC', toCoin: 'ETH', coinName: 'Bitcoin', amount: 0.1, value: 6723.46, fee: 10.09, status: 'completed', date: '2024-01-10', time: '14:45:00' },
  { id: 12, type: 'sell', coin: 'DOGE', coinName: 'Dogecoin', amount: 5000, value: 617.00, fee: 0.93, status: 'completed', date: '2024-01-10', time: '08:30:00' },
];

const getTypeIcon = (type) => {
  switch (type) {
    case 'buy': return 'fa-arrow-down';
    case 'sell': return 'fa-arrow-up';
    case 'swap': return 'fa-arrow-right-arrow-left';
    default: return 'fa-circle';
  }
};

const getTypeColor = (type) => {
  switch (type) {
    case 'buy': return 'bg-success/10 text-success';
    case 'sell': return 'bg-destructive/10 text-destructive';
    case 'swap': return 'bg-primary/10 text-primary';
    default: return 'bg-muted text-muted-foreground';
  }
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'completed':
      return <Badge variant="outline" className="text-xs text-success border-success/30"><i className="fa-solid fa-check mr-1"></i>Completed</Badge>;
    case 'pending':
      return <Badge variant="outline" className="text-xs text-warning border-warning/30"><i className="fa-solid fa-clock mr-1"></i>Pending</Badge>;
    case 'failed':
      return <Badge variant="outline" className="text-xs text-destructive border-destructive/30"><i className="fa-solid fa-xmark mr-1"></i>Failed</Badge>;
    default:
      return null;
  }
};

export const Transactions = () => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');

  const getStatusBadgeLocal = (status) => {
    switch (status) {
      case 'completed':
        return <Badge variant="outline" className="text-xs text-success border-success/30"><i className="fa-solid fa-check mr-1"></i>{t('completed')}</Badge>;
      case 'pending':
        return <Badge variant="outline" className="text-xs text-warning border-warning/30"><i className="fa-solid fa-clock mr-1"></i>{t('pending')}</Badge>;
      case 'failed':
        return <Badge variant="outline" className="text-xs text-destructive border-destructive/30"><i className="fa-solid fa-xmark mr-1"></i>{t('failed')}</Badge>;
      default:
        return null;
    }
  };

  // Calculate summary stats
  const completedTx = allTransactions.filter(tx => tx.status === 'completed');
  const totalVolume = completedTx.reduce((sum, tx) => sum + tx.value, 0);
  const totalFees = completedTx.reduce((sum, tx) => sum + tx.fee, 0);
  const buyVolume = completedTx.filter(tx => tx.type === 'buy').reduce((sum, tx) => sum + tx.value, 0);
  const sellVolume = completedTx.filter(tx => tx.type === 'sell').reduce((sum, tx) => sum + tx.value, 0);

  // Filter transactions
  const filteredTransactions = allTransactions.filter(tx => {
    if (filter !== 'all' && tx.type !== filter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return tx.coin.toLowerCase().includes(query) || tx.coinName.toLowerCase().includes(query);
    }
    return true;
  });

  return (
    <div className="p-4 lg:p-6 space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">{t('transactions')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('viewTransactions')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <i className="fa-solid fa-download mr-2 text-xs"></i>
            {t('exportCsv')}
          </Button>
          <Button variant="outline" size="sm">
            <i className="fa-solid fa-filter mr-2 text-xs"></i>
            {t('filterByType')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t('volume')}</p>
            <p className="text-xl font-semibold font-mono mt-1">${totalVolume.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
            <p className="text-xs text-muted-foreground mt-1">{completedTx.length} {t('transactions').toLowerCase()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t('buy')}</p>
            <p className="text-xl font-semibold font-mono mt-1 text-success">${buyVolume.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
            <p className="text-xs text-muted-foreground mt-1">{completedTx.filter(tx => tx.type === 'buy').length} {t('buy').toLowerCase()}s</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t('sell')}</p>
            <p className="text-xl font-semibold font-mono mt-1 text-destructive">${sellVolume.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
            <p className="text-xs text-muted-foreground mt-1">{completedTx.filter(tx => tx.type === 'sell').length} {t('sell').toLowerCase()}s</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t('networkFee')}</p>
            <p className="text-xl font-semibold font-mono mt-1">${totalFees.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">Ø ${(totalFees / completedTx.length).toFixed(2)}/tx</p>
          </CardContent>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card>
        <CardHeader className="pb-3 px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-base">{t('transactionHistory')}</CardTitle>
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative">
                <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"></i>
                <Input
                  placeholder={t('searchCoins')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-sm w-full sm:w-40"
                />
              </div>

              {/* Type Filter */}
              <Tabs value={filter} onValueChange={setFilter}>
                <TabsList className="h-8">
                  <TabsTrigger value="all" className="text-xs px-3 h-7">{t('all')}</TabsTrigger>
                  <TabsTrigger value="buy" className="text-xs px-3 h-7">{t('buy')}</TabsTrigger>
                  <TabsTrigger value="sell" className="text-xs px-3 h-7">{t('sell')}</TabsTrigger>
                  <TabsTrigger value="swap" className="text-xs px-3 h-7">{t('swap')}</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0 sm:px-2">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground">
                  <th className="text-left font-medium py-3 px-4">{t('type')}</th>
                  <th className="text-left font-medium py-3 px-4">{t('asset')}</th>
                  <th className="text-right font-medium py-3 px-4">{t('amount')}</th>
                  <th className="text-right font-medium py-3 px-4">{t('price')}</th>
                  <th className="text-right font-medium py-3 px-4 hidden sm:table-cell">{t('networkFee')}</th>
                  <th className="text-left font-medium py-3 px-4 hidden md:table-cell">{t('date')}</th>
                  <th className="text-left font-medium py-3 px-4">{t('status')}</th>
                  <th className="text-right font-medium py-3 px-4 hidden lg:table-cell"></th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx) => (
                  <tr key={tx.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4">
                      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", getTypeColor(tx.type))}>
                        <i className={cn("fa-solid text-sm", getTypeIcon(tx.type))}></i>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{tx.coin}</span>
                        {tx.type === 'swap' && (
                          <>
                            <i className="fa-solid fa-arrow-right text-[10px] text-muted-foreground"></i>
                            <span className="font-medium text-sm">{tx.toCoin}</span>
                          </>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground capitalize">{t(tx.type)}</p>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={cn(
                        "font-mono text-sm",
                        tx.type === 'buy' ? 'text-success' : tx.type === 'sell' ? 'text-destructive' : ''
                      )}>
                        {tx.type === 'buy' ? '+' : tx.type === 'sell' ? '-' : ''}{tx.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-sm">
                      ${tx.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-muted-foreground hidden sm:table-cell">
                      ${tx.fee.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      <div className="text-sm">{tx.date}</div>
                      <div className="text-xs text-muted-foreground">{tx.time}</div>
                    </td>
                    <td className="py-3 px-4">
                      {getStatusBadgeLocal(tx.status)}
                    </td>
                    <td className="py-3 px-4 text-right hidden lg:table-cell">
                      <Button variant="ghost" size="sm" className="h-7 text-xs">
                        <i className="fa-solid fa-ellipsis"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-sm text-muted-foreground">
              {filteredTransactions.length} {t('of')} {allTransactions.length} {t('transactions').toLowerCase()}
            </p>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-7" disabled>
                <i className="fa-solid fa-chevron-left text-xs"></i>
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 bg-primary/10 text-primary">1</Button>
              <Button variant="outline" size="sm" className="h-7 w-7">2</Button>
              <Button variant="outline" size="sm" className="h-7">
                <i className="fa-solid fa-chevron-right text-xs"></i>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Transactions;
